import i18next from 'i18next';

const API_BASE = '/api/v1/auth';

export interface User {
  id: string;
  email: string;
  displayName: string | null;
  emailVerified: boolean;
  tier: 'free' | 'pro' | 'max';
  isAdmin?: boolean;
  createdAt?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  emailSent?: boolean;
}

export interface RegisterData {
  email: string;
  password: string;
  displayName?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

class AuthAPI {
  private accessToken: string | null = null;

  constructor() {
    // Try to restore token from localStorage
    this.accessToken = localStorage.getItem('accessToken');
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
    if (token) {
      localStorage.setItem('accessToken', token);
    } else {
      localStorage.removeItem('accessToken');
    }
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || i18next.t('errors.defaultError'));
    }

    return data;
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    this.setAccessToken(response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);

    return response;
  }

  async login(data: LoginData): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    this.setAccessToken(response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);

    return response;
  }

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refreshToken');

    try {
      await this.request('/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      // Ignore errors on logout
    }

    this.setAccessToken(null);
    localStorage.removeItem('refreshToken');
  }

  async refreshToken(): Promise<AuthTokens> {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    const response = await fetch(`${API_BASE}/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    const data = await response.json();

    if (!response.ok) {
      this.setAccessToken(null);
      localStorage.removeItem('refreshToken');
      throw new Error(data.error || 'Session expired');
    }

    this.setAccessToken(data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);

    return data;
  }

  async getMe(): Promise<User> {
    return this.request<User>('/me');
  }

  async updateProfile(data: { displayName?: string }): Promise<User> {
    return this.request<User>('/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async loginWithGoogle(idToken: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/google', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    });

    this.setAccessToken(response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);

    return response;
  }

  async getGoogleConfig(): Promise<{ enabled: boolean; clientId: string | null }> {
    return this.request<{ enabled: boolean; clientId: string | null }>('/google/config');
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  // Email verification methods
  async verifyEmail(token: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendVerification(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  // Password reset methods
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }
}

export const authAPI = new AuthAPI();
